/**
 * Adhoc Task / Compliance Modal
 * ----------------------------------------------------------------
 * Captures a fund-specific one-off (or recurring) compliance line
 * item that lives OUTSIDE the shared Compliance Database. It will
 * never appear in another fund's assignment matrix.
 *
 * Supports both one-time and recurring; recurring supports either
 * a specific first due date or a relative (anchor + days-after) rule.
 */
import React, { useEffect, useState } from 'react';
import { X, Loader, Zap } from 'lucide-react';
import { complianceAPI, fundAPI } from '../../services/api';
import RichTextEditor from '../email/RichTextEditor';

const FREQUENCIES = [
  { value: 'biweekly', label: 'Every 15 days (Biweekly)' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual (Every 6 months)' },
  { value: 'yearly', label: 'Yearly' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ANCHOR_PRESETS = [
  { label: 'FY End', month: 3, day: 31 },
  { label: 'Half-Year End', month: 9, day: 30 },
  { label: 'CY End', month: 12, day: 31 },
  { label: 'Q1 End', month: 6, day: 30 },
  { label: 'Q3 End', month: 12, day: 31 },
];

const AdhocTaskModal = ({ open, onClose, defaultFundId, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [funds, setFunds] = useState([]);
  const [team, setTeam] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    fund_id: '',
    name: '',
    description_html: '',
    assignee_id: '',
    compliance_type: 'one_time',
    frequency: 'monthly',
    due_date: '',
    due_date_rule: 'specific',
    anchor_month: '',
    anchor_day: '',
    days_after_anchor: '',
    anchor_label: '',
    internal_due_offset_days: '',
  });

  useEffect(() => {
    if (!open) return;
    setError(null);
    Promise.all([fundAPI.getAll(), complianceAPI.listTeamMembers()])
      .then(([f, t]) => { setFunds(f.data || []); setTeam(t.data || []); });
    setForm(f => ({ ...f, fund_id: defaultFundId || f.fund_id }));
  }, [open, defaultFundId]);

  if (!open) return null;
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setError(null);
    if (!form.fund_id) { setError('Please choose a Fund.'); return; }
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (form.compliance_type === 'one_time' && !form.due_date) {
      setError('One-time task needs a Due Date.'); return;
    }
    if (form.compliance_type === 'recurring') {
      if (!form.frequency) { setError('Pick a Frequency.'); return; }
      if (form.due_date_rule === 'relative') {
        if (!form.anchor_month || !form.anchor_day || form.days_after_anchor === '') {
          setError('Relative schedule needs anchor date and days after.'); return;
        }
      } else if (!form.due_date) {
        setError('Pick a First Due Date (or switch to relative schedule).'); return;
      }
    }

    const payload = {
      fund_id: form.fund_id,
      name: form.name.trim(),
      description_html: form.description_html || null,
      assignee_id: form.assignee_id || null,
      compliance_type: form.compliance_type,
      frequency: form.compliance_type === 'recurring' ? form.frequency : null,
      due_date: (form.compliance_type === 'recurring' && form.due_date_rule === 'relative') ? null : (form.due_date || null),
      due_date_rule: form.due_date_rule || 'specific',
      anchor_month: form.anchor_month === '' ? null : Number(form.anchor_month),
      anchor_day: form.anchor_day === '' ? null : Number(form.anchor_day),
      days_after_anchor: form.days_after_anchor === '' ? null : Number(form.days_after_anchor),
      anchor_label: form.anchor_label || null,
      internal_due_offset_days: form.internal_due_offset_days === '' ? null : Number(form.internal_due_offset_days),
    };
    setSaving(true);
    try {
      const res = await complianceAPI.createAdhocItem(payload);
      onSaved?.(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="adhoc-task-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div>
            <h2 className="text-lg font-semibold inline-flex items-center gap-2">
              <Zap size={18} /> Add Task / Compliance Item
            </h2>
            <p className="text-xs text-blue-100 mt-0.5">
              Fund-specific adhoc item. Stays attached to the chosen fund only — won't appear in the Compliance Database.
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:opacity-70" data-testid="adhoc-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2" data-testid="adhoc-form-error">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
              <select value={form.fund_id} onChange={e => setF('fund_id', e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-fund">
                <option value="">Choose fund…</option>
                {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select value={form.compliance_type} onChange={e => setF('compliance_type', e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-type">
                <option value="one_time">One-time task</option>
                <option value="recurring">Recurring</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Task / Compliance Name</label>
            <input type="text" value={form.name} onChange={e => setF('name', e.target.value)}
                   className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-name"
                   placeholder="e.g. Submit board minutes to custodian" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
            <RichTextEditor
              value={form.description_html}
              onChange={html => setF('description_html', html)}
              placeholder="Add details, links or context…"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Assignee</label>
              <select value={form.assignee_id} onChange={e => setF('assignee_id', e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-assignee">
                <option value="">— Unassigned —</option>
                {team.map(m => <option key={m.id} value={m.id}>{m.name} ({m.email})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Internal Due (days before Due Date)</label>
              <input type="number" min="0" value={form.internal_due_offset_days}
                     onChange={e => setF('internal_due_offset_days', e.target.value)}
                     className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g. 3"
                     data-testid="adhoc-internal-due" />
            </div>
          </div>

          {form.compliance_type === 'one_time' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setF('due_date', e.target.value)}
                     className="w-full md:w-1/2 border rounded px-3 py-2 text-sm" data-testid="adhoc-due-date" />
            </div>
          )}

          {form.compliance_type === 'recurring' && (
            <div className="space-y-3 bg-blue-50 p-3 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                  <select value={form.frequency} onChange={e => setF('frequency', e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-frequency">
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Due Date Rule</label>
                  <div className="inline-flex rounded-lg border overflow-hidden text-sm w-full">
                    <button type="button" onClick={() => setF('due_date_rule', 'specific')}
                            className={`flex-1 px-3 py-2 ${form.due_date_rule === 'specific' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                            data-testid="adhoc-rule-specific">Specific date</button>
                    <button type="button" onClick={() => setF('due_date_rule', 'relative')}
                            className={`flex-1 px-3 py-2 border-l ${form.due_date_rule === 'relative' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                            data-testid="adhoc-rule-relative">Days after a recurring date</button>
                  </div>
                </div>
              </div>

              {form.due_date_rule === 'specific' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">First Due Date</label>
                  <input type="date" value={form.due_date} onChange={e => setF('due_date', e.target.value)}
                         className="w-full md:w-1/2 border rounded px-3 py-2 text-sm" data-testid="adhoc-first-due" />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {ANCHOR_PRESETS.map(p => (
                      <button key={p.label} type="button"
                              onClick={() => setForm(f => ({ ...f, anchor_month: p.month, anchor_day: p.day, anchor_label: p.label }))}
                              className={`px-2.5 py-1 text-xs rounded-full border transition ${
                                Number(form.anchor_month) === p.month && Number(form.anchor_day) === p.day
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-700 hover:bg-blue-100 border-gray-300'
                              }`}
                              data-testid={`adhoc-anchor-preset-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        {p.label} ({p.day}-{MONTH_NAMES[p.month - 1]})
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Anchor Month</label>
                      <select value={form.anchor_month}
                              onChange={e => setF('anchor_month', e.target.value ? Number(e.target.value) : '')}
                              className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-anchor-month">
                        <option value="">—</option>
                        {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Anchor Day</label>
                      <input type="number" min="1" max="31" value={form.anchor_day}
                             onChange={e => setF('anchor_day', e.target.value ? Number(e.target.value) : '')}
                             className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-anchor-day" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Days After</label>
                      <input type="number" min="0" value={form.days_after_anchor}
                             onChange={e => setF('days_after_anchor', e.target.value === '' ? '' : Number(e.target.value))}
                             className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-days-after"
                             placeholder="e.g. 180" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Label (optional)</label>
                      <input type="text" value={form.anchor_label}
                             onChange={e => setF('anchor_label', e.target.value)}
                             className="w-full border rounded px-3 py-2 text-sm" data-testid="adhoc-anchor-label"
                             placeholder="e.g. FY End" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={saving}
                  className="px-4 py-2 border rounded text-sm" data-testid="adhoc-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="adhoc-save">
            {saving && <Loader size={14} className="animate-spin" />}
            Create Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdhocTaskModal;
